// Reusable client-side report export (PDF / Excel).
// Libraries are loaded on demand so they don't bloat the main bundle.
//
// columns: [{ title, dataIndex, render?, exportValue? }]
//   exportValue(val, row) -> plain text used in the file. If omitted, `render`
//   is used when it returns a string/number, otherwise the raw value.
//   numeric: true -> right-aligned in PDF, written as a real number in Excel.
// meta: { title, subtitle?, filters?: [{ label, value }], summary?: [{ label, value }], fileName }
//   summary lines are printed right after "Total Records".

const toCellText = (col, row) => {
  const raw = row?.[col.dataIndex];
  let out;
  if (col.exportValue) out = col.exportValue(raw, row);
  else if (col.render) {
    const rendered = col.render(raw, row);
    out = typeof rendered === "string" || typeof rendered === "number" ? rendered : raw;
  } else out = raw;
  return out === null || out === undefined || out === "" ? "-" : String(out);
};

const buildRows = (columns, data) => data.map((row) => columns.map((col) => toCellText(col, row)));

const timestamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return {
    display: `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`,
    file: `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`
  };
};

const filterLine = (filters = []) =>
  filters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "")
    .map((f) => `${f.label}: ${f.value}`)
    .join("   |   ");

export const exportToPdf = async (columns, data, meta) => {
  const [{ jsPDF }, { autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const ts = timestamp();
  // Wide reports go on A3 landscape so all columns fit on one page width.
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: columns.length > 10 ? "a3" : "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(meta.title, 40, 40);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  let y = 58;
  if (meta.subtitle) { doc.text(meta.subtitle, 40, y); y += 14; }
  const filters = filterLine(meta.filters);
  if (filters) { doc.text(filters, 40, y); y += 14; }
  doc.setFont("helvetica", "bold");
  doc.text(`Total Records: ${data.length}`, 40, y);
  (meta.summary || []).forEach((s) => { y += 14; doc.text(`${s.label}: ${s.value}`, 40, y); });
  doc.setFont("helvetica", "normal");
  doc.text(`Generated: ${ts.display}`, pageWidth - 40, 40, { align: "right" });

  const columnStyles = {};
  columns.forEach((c, i) => { if (c.numeric) columnStyles[i] = { halign: "right" }; });

  autoTable(doc, {
    startY: y + 10,
    head: [columns.map((c) => c.title)],
    body: buildRows(columns, data),
    columnStyles,
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: [33, 37, 41], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 246, 248] },
    margin: { left: 40, right: 40 },
    didDrawPage: () => {
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - 40, pageHeight - 20, { align: "right" });
    }
  });

  doc.save(`${meta.fileName}_${ts.file}.pdf`);
};

export const exportToExcel = async (columns, data, meta) => {
  const ExcelJS = (await import("exceljs")).default;
  const ts = timestamp();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Report");
  const lastCol = columns.length;

  const titleRow = ws.addRow([meta.title]);
  titleRow.font = { bold: true, size: 14 };
  ws.mergeCells(1, 1, 1, lastCol);

  const info = [meta.subtitle, filterLine(meta.filters)].filter(Boolean);
  const totals = [`Total Records: ${data.length}`, ...(meta.summary || []).map((s) => `${s.label}: ${s.value}`)];
  [...info, ...totals, `Generated: ${ts.display}`].forEach((line) => {
    const r = ws.addRow([line]);
    r.font = totals.includes(line) ? { bold: true, size: 10 } : { italic: true, size: 9 };
    ws.mergeCells(r.number, 1, r.number, lastCol);
  });
  ws.addRow([]);

  const headerRow = ws.addRow(columns.map((c) => c.title));
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF212529" } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
  });

  const rows = buildRows(columns, data);
  data.forEach((src, ri) => {
    const values = rows[ri].map((text, ci) => {
      const raw = Number(src?.[columns[ci].dataIndex]);
      return columns[ci].numeric && text !== "-" && !isNaN(raw) ? raw : text;
    });
    const row = ws.addRow(values);
    row.eachCell((cell, ci) => {
      if (columns[ci - 1]?.numeric && typeof cell.value === "number") {
        cell.numFmt = "#,##0.00";
        cell.alignment = { horizontal: "right" };
      }
      cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    });
  });

  ws.autoFilter = { from: { row: headerRow.number, column: 1 }, to: { row: headerRow.number, column: lastCol } };
  ws.views = [{ state: "frozen", ySplit: headerRow.number }];
  columns.forEach((c, i) => {
    const longest = Math.max(c.title.length, ...rows.map((r) => r[i].length));
    ws.getColumn(i + 1).width = Math.min(Math.max(longest + 2, 10), 45);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${meta.fileName}_${ts.file}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
